import { useLiveQuery } from "dexie-react-hooks"
import type { AgentExecutionLight } from "@stockline/shared/types/ai/execution"
import { offlineDb } from "../../../lib/offline/db"
import { EXECUTION_TABLE } from "../../../lib/offline/resources/ai/execution/db"
import {
  SUBAGENT_AGENT_TYPES,
  isSubagentExecution,
} from "./isSubagentExecution"

const ACTIVE_STATUSES = ["pending", "running", "paused", "waiting_input"]

/**
 * One-shot read of every top-level execution's primary key (sub-agent rows
 * excluded). Use for operations that need the full id set (e.g. "dismiss all")
 * regardless of how many pages are currently loaded into the panel.
 */
export async function getAllExecutionIds(): Promise<string[]> {
  return (await offlineDb
    .table<AgentExecutionLight>(EXECUTION_TABLE)
    .where("agentType")
    .noneOf(SUBAGENT_AGENT_TYPES)
    .primaryKeys()) as string[]
}

/**
 * Reactive count of top-level executions whose status is non-terminal
 * (pending / running / paused / waiting_input). Sub-agent rows are excluded so
 * the badge matches the visible list.
 */
export function useActiveExecutionsCount(): number {
  const result = useLiveQuery(
    () =>
      offlineDb
        .table<AgentExecutionLight>(EXECUTION_TABLE)
        .where("status")
        .anyOf(ACTIVE_STATUSES)
        .filter((e) => !isSubagentExecution(e))
        .count(),
    [],
    0
  )
  return result ?? 0
}

/**
 * Reactive total count of top-level executions in the offline store
 * (sub-agent rows excluded so the count matches the visible list).
 */
export function useExecutionsTotalCount(): number {
  const result = useLiveQuery(
    () =>
      offlineDb
        .table<AgentExecutionLight>(EXECUTION_TABLE)
        .where("agentType")
        .noneOf(SUBAGENT_AGENT_TYPES)
        .count(),
    [],
    0
  )
  return result ?? 0
}
